import React, { useState } from 'react'
import { FaCheckCircle, FaInfoCircle, FaMinus, FaPlus } from 'react-icons/fa'
import { BottomSheet } from '../../../commons/BottomSheet'

const MIN_UNITS = 1
const MAX_UNITS = 20

// Materials note from the price list's includesMaterials flag.
// Nothing is shown when the field is missing (the older price-list API).
export const MaterialsBadge = ({ includesMaterials, size = 'sm' }) => {
    if (typeof includesMaterials !== 'boolean') return null
    const base = size === 'xs'
        ? 'inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full'
        : 'inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full'
    return includesMaterials ? (
        <span className={`${base} bg-green-50 text-green-700 border border-green-200`}>
            <FaCheckCircle aria-hidden="true" /> Materials included
        </span>
    ) : (
        <span className={`${base} bg-amber-50 text-amber-800 border border-amber-200`}>
            <FaInfoCircle aria-hidden="true" /> Labour only – you provide the materials
        </span>
    )
}

// The step shown before booking any quick-booked handyman job:
//  - the job's summary (what is covered and excluded),
//  - whether materials are included (includesMaterials),
//  - a unit stepper when the job is priced per unit (unitRequired).
// It renders BottomSheet.Content and .Footer, so it has to sit inside a
// BottomSheet: either HandymanDealItems' own sheet (as a view swap) or
// HandymanJobDetailsSheet below.
//
// onConfirm(units) receives the unit count, or null when units aren't asked.
export const HandymanJobDetailsStep = ({ item, askUnits, unitsQuestion, onConfirm, onBack }) => {
    const [ units, setUnits ] = useState(MIN_UNITS)
    const change = (delta) => setUnits((u) => Math.min(MAX_UNITS, Math.max(MIN_UNITS, u + delta)))

    return (
        <>
            <BottomSheet.Content>
                <div className="space-y-5 py-2">
                    <div className="space-y-2">
                        <p className="text-base font-semibold text-gray-900">{item?.itemName}</p>
                        {item?.subCategory && <p className="text-xs text-gray-400">{item.subCategory}</p>}
                        <MaterialsBadge includesMaterials={item?.includesMaterials} />
                        {item?.summary &&
                            <p className="text-sm text-gray-600 leading-relaxed">{item.summary}</p>
                        }
                    </div>

                    {askUnits &&
                        <div className="border-t border-gray-100 pt-4">
                            <p id="units-label" className="text-sm font-medium text-gray-700 text-center mb-3">
                                {unitsQuestion || 'Specify the number of units.'}
                            </p>
                            <div className="flex items-center justify-center gap-5" role="group" aria-labelledby="units-label">
                                <button
                                    type="button"
                                    onClick={() => change(-1)}
                                    disabled={units <= MIN_UNITS}
                                    aria-label="Decrease number of units"
                                    className="w-12 h-12 rounded-full border border-gray-300 flex items-center justify-center text-gray-700 disabled:opacity-30"
                                >
                                    <FaMinus aria-hidden="true" />
                                </button>
                                <output aria-live="polite" className="w-16 text-center text-3xl font-bold text-gray-900 tabular-nums">
                                    {units}
                                </output>
                                <button
                                    type="button"
                                    onClick={() => change(1)}
                                    disabled={units >= MAX_UNITS}
                                    aria-label="Increase number of units"
                                    className="w-12 h-12 rounded-full border border-orange-300 bg-orange-50 flex items-center justify-center text-orange-600 disabled:opacity-30"
                                >
                                    <FaPlus aria-hidden="true" />
                                </button>
                            </div>
                            <p className="mt-4 text-sm text-gray-500 leading-relaxed bg-gray-50 border border-gray-100 rounded-xl p-3 text-center">
                                Your total for {units} {units === 1 ? 'unit' : 'units'} is calculated on the next step, before you confirm.
                            </p>
                        </div>
                    }
                </div>
            </BottomSheet.Content>
            <BottomSheet.Footer>
                <div className="flex gap-3">
                    <button
                        type="button"
                        onClick={onBack}
                        className="flex-1 min-h-12 px-4 rounded-xl font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors"
                    >
                        Back
                    </button>
                    <button
                        type="button"
                        onClick={() => onConfirm(askUnits ? units : null)}
                        className="flex-1 min-h-12 px-4 rounded-xl font-semibold text-white bg-linear-to-r from-orange-500 to-amber-500 shadow-lg shadow-orange-200"
                    >
                        Continue
                    </button>
                </div>
            </BottomSheet.Footer>
        </>
    )
}

// Stand-alone sheet version, for the quick-book tiles and the job cards that
// don't already live inside a BottomSheet.
export const HandymanJobDetailsSheet = ({ onClose, ...props }) => (
    <BottomSheet isOpen={true} onClose={onClose} maxWidth="md">
        <BottomSheet.Header>
            <BottomSheet.Title>Service Details</BottomSheet.Title>
            <BottomSheet.Subtitle>Check what is included, then continue to book</BottomSheet.Subtitle>
        </BottomSheet.Header>
        <HandymanJobDetailsStep {...props} onBack={onClose} />
    </BottomSheet>
)
